import { motion } from "framer-motion";
import {
  Compass,
  Cpu,
  LayoutTemplate,
  BarChart3,
  Workflow,
  Sparkles,
} from "lucide-react";
import { CAPABILITIES } from "@/constants/testIds";

const ITEMS = [
  {
    slug: "strategic-advisory",
    icon: Compass,
    title: "Strategic Advisory",
    body: "Market reads, business model design, and the hard calls on where to place bets.",
    chip: "bg-brand/15 text-brand",
    border: "border-t-brand",
  },
  {
    slug: "agentic-ai",
    icon: Cpu,
    title: "Agentic AI & Automation",
    body: "Autonomous agents and workflows that take real operational work off human plates.",
    chip: "bg-brand-indigo/15 text-brand-indigo",
    border: "border-t-brand-indigo",
  },
  {
    slug: "product-engineering",
    icon: LayoutTemplate,
    title: "Product Engineering",
    body: "Full-stack builds, from first prototype to production-grade software.",
    chip: "bg-brand-teal/15 text-brand-teal",
    border: "border-t-brand-teal",
  },
  {
    slug: "data-analytics",
    icon: BarChart3,
    title: "Data & Analytics Systems",
    body: "Dashboards and forecasting engines that turn raw operational data into decisions.",
    chip: "bg-brand-rose/15 text-brand-rose",
    border: "border-t-brand-rose",
  },
  {
    slug: "operating-model",
    icon: Workflow,
    title: "Operating Model Design",
    body: "Org and process design so strategy, tech and product stay in lockstep after we leave.",
    chip: "bg-brand/15 text-brand",
    border: "border-t-brand",
  },
  {
    slug: "experience-design",
    icon: Sparkles,
    title: "Experience & Design",
    body: "Interfaces and interactions crafted with the same rigor as the systems behind them.",
    chip: "bg-brand-indigo/15 text-brand-indigo",
    border: "border-t-brand-indigo",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid={CAPABILITIES.section}
      className="relative py-24 md:py-36 px-6 md:px-10 max-w-7xl mx-auto"
    >
      <div
        className="absolute top-10 right-0 w-[320px] h-[320px] rounded-full bg-brand-teal/10 blur-[100px] pointer-events-none"
        aria-hidden="true"
      />
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeUp}
        className="mb-14 md:mb-20 max-w-xl relative"
      >
        <span className="text-xs tracking-[0.25em] uppercase font-medium text-brand">
          Capabilities
        </span>
        <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-ink mt-4">
          Six disciplines, applied as one.
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 border border-border/70">
        {ITEMS.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.slug}
              data-testid={CAPABILITIES.card(item.slug)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              transition={{ delay: (i % 3) * 0.08 }}
              className={`group border-border/70 border-b md:border-r [&:nth-child(3n)]:md:border-r-0 border-t-2 ${item.border} p-8 md:p-10 bg-card hover:bg-muted/60 transition-colors duration-300`}
            >
              <div className={`inline-flex items-center justify-center w-11 h-11 rounded-lg ${item.chip} mb-6 transition-transform duration-300 group-hover:-translate-y-1`}>
                <Icon size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-lg tracking-tight text-ink mb-2">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {item.body}
              </p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
